import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ArrowLeft, Plugs, CheckCircle, Lightning, WifiHigh, Clipboard, SpinnerGap, ArrowsClockwise, Play, StopCircle, CloudRain, MagnifyingGlass } from '@phosphor-icons/react';
import { API_URL } from '../config/api';
import { SEO } from '../components/SEO';

const TOKEN_LS_KEY = 'tp_rachio_token';

const RachioConnectPage = () => {
  const navigate = useNavigate();
  const [token, setToken] = useState('');
  const [storedToken, setStoredToken] = useState('');   // session token (plaintext, localStorage or in-memory)
  const [persistedOnServer, setPersistedOnServer] = useState(false);
  const [saveToAccount, setSaveToAccount] = useState(true);  // checkbox — default on
  const [mode, setMode] = useState('stub'); // stub | live
  const [device, setDevice] = useState(null);
  const [loading, setLoading] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [translation, setTranslation] = useState(null);
  const [translating, setTranslating] = useState(false);
  // Live control state
  const [zoneRunning, setZoneRunning] = useState(null); // zone_id currently being submitted
  const [stopping, setStopping] = useState(false);
  const [delaying, setDelaying] = useState(false);
  const [probing, setProbing] = useState(false);
  const [probeResult, setProbeResult] = useState(null);

  // Run a single zone NOW (prompts for minutes, defaults to 5)
  const runZoneNow = async (zone) => {
    if (mode !== 'live') {
      toast.error('Connect a real Rachio token first.');
      return;
    }
    const minsRaw = window.prompt(`Run "${zone.name}" for how many minutes?`, '5');
    if (minsRaw === null) return;
    const mins = parseInt(minsRaw, 10);
    if (isNaN(mins) || mins < 1 || mins > 180) {
      toast.error('Enter a number between 1 and 180.');
      return;
    }
    setZoneRunning(zone.id);
    try {
      await axios.post(
        `${API_URL}/api/rachio/zone/start`,
        { zone_id: zone.id, duration_seconds: mins * 60 },
        { withCredentials: true }
      );
      toast.success(`${zone.name} running for ${mins} min — should start within 5 sec.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not start zone');
    } finally {
      setZoneRunning(null);
    }
  };

  // Emergency stop — halts every zone immediately
  const stopAllWater = async () => {
    if (!device || mode !== 'live') return;
    if (!window.confirm('Stop all watering on this controller right now?')) return;
    setStopping(true);
    try {
      await axios.post(
        `${API_URL}/api/rachio/device/stop`,
        { device_id: device.id },
        { withCredentials: true }
      );
      toast.success('All zones stopped.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Stop failed');
    } finally {
      setStopping(false);
    }
  };

  // Set a rain delay (hours)
  const setRainDelay = async () => {
    if (!device || mode !== 'live') return;
    const hrsRaw = window.prompt('Skip watering for how many hours? (1–168)', '24');
    if (hrsRaw === null) return;
    const hrs = parseInt(hrsRaw, 10);
    if (isNaN(hrs) || hrs < 1 || hrs > 168) {
      toast.error('Enter 1 to 168.');
      return;
    }
    setDelaying(true);
    try {
      await axios.post(
        `${API_URL}/api/rachio/device/rain_delay`,
        { device_id: device.id, hours: hrs },
        { withCredentials: true }
      );
      toast.success(`Rain delay set for ${hrs} hours.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Rain delay failed');
    } finally {
      setDelaying(false);
    }
  };

  // Probe what this PAT can actually do
  const runCapabilityProbe = async () => {
    setProbing(true);
    setProbeResult(null);
    try {
      const r = await axios.get(`${API_URL}/api/rachio/probe`, { withCredentials: true });
      setProbeResult(r.data);
      toast.success(`Probe done — ${r.data.summary.endpoints_ok}/${r.data.summary.endpoints_tried} endpoints reachable`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Probe failed');
    } finally {
      setProbing(false);
    }
  };

  useEffect(() => {
    (async () => {
      // Check server-side persisted token first — survives browser clears
      try {
        const p = await axios.get(`${API_URL}/api/rachio/persisted`, { withCredentials: true });
        if (p.data?.has_token) {
          setPersistedOnServer(true);
          // Server will resolve the token from DB on zones call — no header needed
          await loadZones(null);
          return;
        }
      } catch {
        /* not signed in or server issue — fall through to local */
      }

      const saved = localStorage.getItem(TOKEN_LS_KEY);
      if (saved) {
        setStoredToken(saved);
        setToken(saved);
        loadZones(saved);
      } else {
        loadZones(null);
      }
    })();
  }, []);

  const loadZones = async (t) => {
    setLoading(true);
    try {
      const headers = t ? { 'X-Rachio-Token': t } : {};
      const r = await axios.get(`${API_URL}/api/rachio/zones`, { headers, withCredentials: true });
      setDevice(r.data.device);
      setMode(r.data.mode);
      if (r.data.persisted) setPersistedOnServer(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load zones.');
      if (t) {
        // bad token — fall back to stub
        localStorage.removeItem(TOKEN_LS_KEY);
        setStoredToken('');
        loadZones(null);
      }
    } finally { setLoading(false); }
  };

  const onConnect = async () => {
    if (!token || token.length < 10) {
      toast.error('Paste your Rachio Personal Access Token first.');
      return;
    }
    setConnecting(true);
    try {
      const r = await axios.post(`${API_URL}/api/rachio/connect`, { token });
      // Always keep a local copy for the current session
      localStorage.setItem(TOKEN_LS_KEY, token);
      setStoredToken(token);
      setDevice(r.data.primary_device);
      setMode('live');

      // Optionally save to the user's account so it survives browser clears
      if (saveToAccount) {
        try {
          await axios.post(
            `${API_URL}/api/rachio/persist`,
            { token },
            { withCredentials: true },
          );
          setPersistedOnServer(true);
          toast.success(`Connected + saved to your account — ${r.data.primary_device.zones.length} zones`);
        } catch (pe) {
          // Sign-in required for persist — connection still works for session
          toast.success(`Connected to ${r.data.primary_device.name} (${r.data.primary_device.zones.length} zones)`);
          if (pe?.response?.status === 401) {
            toast('Sign in to save your Rachio token across devices.', { icon: 'ℹ️' });
          }
        }
      } else {
        toast.success(`Connected to ${r.data.primary_device.name} (${r.data.primary_device.zones.length} zones)`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Connect failed. Check your token.');
    } finally { setConnecting(false); }
  };

  const onDisconnect = async () => {
    localStorage.removeItem(TOKEN_LS_KEY);
    // Also clear server-side if persisted (ignore 401 — just means not signed in)
    try {
      await axios.delete(`${API_URL}/api/rachio/persisted`, { withCredentials: true });
    } catch {
      /* ignore */
    }
    setPersistedOnServer(false);
    setStoredToken('');
    setToken('');
    setDevice(null);
    setTranslation(null);
    loadZones(null);
    toast('Disconnected. Back to stub preview mode.');
  };

  const onTranslate = async () => {
    if (!device?.zones?.length) return;
    setTranslating(true);
    try {
      // Build a demo payload from the device's zones. In a real flow we'd
      // pull this from the user's most recent Water Budget compute.
      const demoZones = device.zones.map((z) => {
        const cat = (z.nozzle_category || '').toUpperCase();
        const runtime = cat === 'ROTARY_NOZZLE' ? 24 : cat === 'EMITTER' ? 18 : cat === 'BUBBLER' ? 10 : 9;
        const cycles = cat === 'FIXED_SPRAY' ? 3 : cat === 'ROTARY_NOZZLE' ? 2 : 1;
        return {
          name: z.name,
          nozzle_type: z.nozzle || cat,
          runtime_minutes: runtime,
          precipitation_rate_in_hr: z.inches_per_hour,
          cycle_and_soak: cycles > 1 ? { cycles, minutes_per_cycle: Math.ceil(runtime / cycles), soak_minutes_between: 30 } : { cycles: 1, minutes_per_cycle: runtime, soak_minutes_between: 0 },
        };
      });
      const r = await axios.post(`${API_URL}/api/rachio/translate`, {
        zones: demoZones,
        allowed_days: ['Thursday', 'Sunday'],
        start_time: '05:00',
        summary: 'Demo translation — plug this into your Water Budget output to personalize.',
      });
      setTranslation(r.data);
    } catch (e) {
      toast.error('Translation failed.');
    } finally { setTranslating(false); }
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied');
  };

  return (
    <div className="min-h-screen bg-background" data-testid="rachio-connect-page">
      <SEO title="Connect Rachio — TurfPulse" description="Connect your Rachio 3 to TurfPulse and push physics-based schedules." />

      <header className="border-b border-border/60 bg-card/80 backdrop-blur-sm sticky top-0 z-30">
        <div className="max-w-5xl mx-auto flex items-center justify-between px-4 py-3">
          <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="rachio-back-btn">
            <ArrowLeft size={14} /> Back
          </button>
          <span className="text-sm font-bold">Rachio Connect</span>
          <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${mode === 'live' ? 'bg-emerald-500/20 text-emerald-700 dark:text-emerald-400' : 'bg-amber-500/20 text-amber-700 dark:text-amber-400'}`} data-testid="rachio-mode-badge">
            {mode === 'live' ? '● LIVE' : '● STUB'}
          </span>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8 space-y-6">
        <div className="space-y-2">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium">
            <Plugs size={12} weight="fill" /> Rachio Public API
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">Your Rachio, with a better brain.</h1>
          <p className="text-base text-muted-foreground">
            Paste your Personal Access Token once. We'll pull your zones live, then translate the Water Budget you already built into the exact schedule to punch into the Rachio app.
          </p>
        </div>

        {/* Token connect */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Lightning size={16} weight="fill" className="text-primary" /> Personal Access Token
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {mode === 'stub' ? (
              <>
                <p className="text-xs text-muted-foreground">
                  Get your token at <a href="https://app.rach.io/" target="_blank" rel="noopener noreferrer" className="text-primary underline">app.rach.io</a> → Account Settings → Get API Key.
                </p>
                <div className="flex flex-col sm:flex-row gap-2">
                  <Input
                    type="password"
                    placeholder="Paste token…"
                    value={token}
                    onChange={(e) => setToken(e.target.value)}
                    className="font-mono text-xs"
                    data-testid="rachio-token-input"
                  />
                  <Button onClick={onConnect} disabled={connecting} data-testid="rachio-connect-btn">
                    {connecting ? <SpinnerGap size={14} className="animate-spin mr-1" /> : <Plugs size={14} className="mr-1" />}
                    Connect
                  </Button>
                </div>
                <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer select-none" data-testid="rachio-save-to-account-label">
                  <input
                    type="checkbox"
                    checked={saveToAccount}
                    onChange={(e) => setSaveToAccount(e.target.checked)}
                    className="h-3.5 w-3.5"
                    data-testid="rachio-save-to-account-checkbox"
                  />
                  <span>
                    <strong>Save to my account</strong> — encrypted in our DB so you don't have to re-paste after clearing your browser.
                  </span>
                </label>
                <p className="text-[11px] text-muted-foreground">
                  No token yet? That's fine — you're seeing a <strong>stub device</strong> below so you can explore the UI. Same flow, same translator.
                </p>
              </>
            ) : (
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 text-sm">
                  <CheckCircle size={18} weight="fill" className="text-emerald-500" />
                  <span>
                    Connected
                    {persistedOnServer ? (
                      <span className="ml-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-emerald-600 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5" data-testid="rachio-persisted-badge">
                        saved to account
                      </span>
                    ) : storedToken ? (
                      <span className="ml-2">· token <code className="bg-muted px-1 rounded text-[10px]">{storedToken.slice(0, 6)}…{storedToken.slice(-4)}</code></span>
                    ) : null}
                  </span>
                </div>
                <Button variant="outline" size="sm" onClick={onDisconnect} data-testid="rachio-disconnect-btn">Disconnect</Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Device + zones */}
        {loading ? (
          <Card><CardContent className="p-8 text-center"><SpinnerGap size={24} className="animate-spin mx-auto text-primary" /></CardContent></Card>
        ) : device ? (
          <Card data-testid="rachio-device-card">
            <CardHeader className="pb-3 flex-row items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <WifiHigh size={16} weight="fill" className={mode === 'live' ? 'text-emerald-500' : 'text-amber-500'} />
                {device.name}
                <span className="text-[10px] font-normal text-muted-foreground">{device.model}</span>
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={() => loadZones(storedToken || null)} data-testid="rachio-refresh-btn">
                <ArrowsClockwise size={14} className="mr-1" /> Refresh
              </Button>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2" data-testid="rachio-zone-grid">
                {device.zones.map((z) => (
                  <div key={z.id} className="border border-border/60 rounded-md p-3 bg-card flex flex-col" data-testid={`rachio-zone-${z.zone_number}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Zone {z.zone_number}</span>
                      {z.enabled === false && <span className="text-[9px] text-red-500">DISABLED</span>}
                    </div>
                    <div className="text-sm font-semibold truncate">{z.name}</div>
                    <div className="text-[11px] text-muted-foreground mt-0.5">
                      {z.nozzle || '—'} {z.inches_per_hour && <>· <span className="font-mono">{z.inches_per_hour}</span> in/hr</>}
                    </div>
                    {z.area_sqft && <div className="text-[10px] text-muted-foreground">{z.area_sqft} sqft</div>}
                    {mode === 'live' && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="mt-2 h-7 text-[11px] gap-1"
                        onClick={() => runZoneNow(z)}
                        disabled={zoneRunning === z.id}
                        data-testid={`rachio-run-zone-${z.zone_number}`}
                      >
                        {zoneRunning === z.id ? (
                          <SpinnerGap size={11} className="animate-spin" />
                        ) : (
                          <Play size={11} weight="fill" />
                        )}
                        Run now
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ) : null}

        {/* Live control panel — only when connected to real Rachio */}
        {device && mode === 'live' && (
          <Card data-testid="rachio-live-control-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Lightning size={16} weight="fill" className="text-amber-500" /> Live Control
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-xs text-muted-foreground">
                Direct control of <span className="font-semibold text-foreground">{device.name}</span> via Rachio's public API. All actions take effect within ~5 seconds.
              </p>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={stopAllWater}
                  disabled={stopping}
                  data-testid="rachio-stop-all-btn"
                >
                  {stopping ? <SpinnerGap size={14} className="animate-spin mr-1" /> : <StopCircle size={14} weight="fill" className="mr-1" />}
                  Emergency Stop
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={setRainDelay}
                  disabled={delaying}
                  data-testid="rachio-rain-delay-btn"
                >
                  {delaying ? <SpinnerGap size={14} className="animate-spin mr-1" /> : <CloudRain size={14} weight="fill" className="mr-1" />}
                  Skip watering (hours)…
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={runCapabilityProbe}
                  disabled={probing}
                  data-testid="rachio-probe-btn"
                >
                  {probing ? <SpinnerGap size={14} className="animate-spin mr-1" /> : <MagnifyingGlass size={14} className="mr-1" />}
                  Probe API capabilities
                </Button>
              </div>
              {probeResult && (
                <div className="border border-border/60 rounded-md p-3 bg-muted/20 text-xs space-y-2" data-testid="rachio-probe-result">
                  <div className="font-semibold">
                    Probe summary: {probeResult.summary.endpoints_ok}/{probeResult.summary.endpoints_tried} endpoints reachable
                    {probeResult.summary.endpoints_403_scope_blocked > 0 && (
                      <span className="text-red-500"> · {probeResult.summary.endpoints_403_scope_blocked} scope-blocked</span>
                    )}
                  </div>
                  <ul className="space-y-1">
                    {probeResult.next_actions.map((a, i) => (
                      <li key={i} className="leading-relaxed">{a}</li>
                    ))}
                  </ul>
                  <details className="pt-1">
                    <summary className="cursor-pointer text-muted-foreground hover:text-foreground">Show raw endpoint matrix</summary>
                    <table className="w-full mt-2 text-[10px] font-mono">
                      <thead className="text-muted-foreground"><tr>
                        <th className="text-left pr-2">Endpoint</th><th className="text-left pr-2">Method</th><th className="text-left">Status</th>
                      </tr></thead>
                      <tbody>
                        {probeResult.results.map((r, i) => (
                          <tr key={i} className="border-t border-border/40">
                            <td className="pr-2 truncate max-w-[200px]">{r.path}</td>
                            <td className="pr-2">{r.method}</td>
                            <td className={r.ok ? 'text-emerald-500' : r.status === 403 ? 'text-red-500' : 'text-amber-500'}>
                              {r.status || 'err'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </details>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Translate */}
        {device && (
          <Card className="border-2 border-primary/30" data-testid="rachio-translate-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Play size={16} weight="fill" className="text-primary" /> Translate Water Budget → Rachio Programs
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-xs text-muted-foreground">
                We'll group your zones into Program A (rotors), B (sprays), C (drip/bubblers) — and give you a paste-ready cheat-sheet for the Rachio app.
              </p>
              <Button onClick={onTranslate} disabled={translating} data-testid="rachio-translate-btn">
                {translating ? <SpinnerGap size={14} className="animate-spin mr-1" /> : <Lightning size={14} className="mr-1" />}
                Generate schedule
              </Button>

              {translation && (
                <div className="space-y-4 pt-3" data-testid="rachio-translation-result">
                  {translation.programs.map((p) => (
                    <div key={p.letter} className="border border-border/60 rounded-md p-4 bg-muted/20" data-testid={`rachio-program-${p.letter}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <span className="text-2xl font-bold font-mono text-primary">Program {p.letter}</span>
                          <span className="text-sm text-muted-foreground ml-2">{p.family}</span>
                        </div>
                        <div className="text-right text-xs">
                          <div className="font-semibold">{p.total_minutes} min total</div>
                          <div className="text-muted-foreground">{p.run_days.join(' · ')} @ {p.start_time}</div>
                        </div>
                      </div>
                      <div className="space-y-1 text-sm">
                        {p.zone_steps.map((s, i) => (
                          <div key={i} className="flex items-center justify-between py-1 border-t border-border/30">
                            <span className="font-medium">{s.zone_name}</span>
                            <span className="font-mono text-xs">{s.display}</span>
                          </div>
                        ))}
                      </div>
                      <Button variant="outline" size="sm" className="mt-3" onClick={() => copy(p.zone_steps.map((s) => `${s.zone_name}: ${s.display}`).join('\n'))} data-testid={`rachio-copy-${p.letter}`}>
                        <Clipboard size={12} className="mr-1" /> Copy to clipboard
                      </Button>
                    </div>
                  ))}

                  {translation.skipped_zones.length > 0 && (
                    <div className="text-xs text-muted-foreground">
                      <strong>Skipped (no runtime this session):</strong> {translation.skipped_zones.join(', ')}
                    </div>
                  )}

                  <div className="bg-muted/30 rounded-md p-3 text-xs space-y-1.5">
                    <div className="font-semibold">How to paste into the Rachio app:</div>
                    <ol className="list-decimal list-inside space-y-1 text-muted-foreground">
                      {translation.paste_instructions.map((s, i) => <li key={i}>{s}</li>)}
                    </ol>
                  </div>

                  {translation.notes.map((n, i) => (
                    <p key={i} className="text-[11px] text-muted-foreground italic">💡 {n}</p>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        <div className="flex flex-col sm:flex-row gap-2 pt-2">
          <Button variant="outline" className="flex-1" onClick={() => navigate('/water-budget')} data-testid="rachio-wb-link">
            Open Water Budget first
          </Button>
          <Button variant="outline" className="flex-1" onClick={() => navigate('/rachio/install-checklist')} data-testid="rachio-install-link">
            Install checklist
          </Button>
          <Button variant="outline" className="flex-1" onClick={() => navigate('/rachio/rebate-helper')} data-testid="rachio-rebate-link">
            Rebate helper
          </Button>
        </div>
      </main>
    </div>
  );
};

export default RachioConnectPage;
